// ── D-TC-WORKFLOW-STATES — the tenant control-work lifecycle, defined ONCE ────────────────────
//
// MI's ruled 8-state machine. It lives in @grc/shared because the api enforces it and the surface
// renders it, and a vocabulary with two homes is a vocabulary that drifts.
//
// ⚠ WORKFLOW IS NOT POSTURE, ALWAYS. This axis answers "where is this piece of work?"; posture
// (`cts_assessment.result`) answers "is the control implemented?". They move independently and are
// legitimately inconsistent-looking — "In Progress · Partially Implemented" is a normal, correct
// row, not a state to reconcile. TC1 built the two tables with no column for the other's answer
// precisely so neither could quietly become a function of the other.
//
// ⚠ THERE IS NO COMPATIBILITY LAYER ANY MORE, AND ITS ABSENCE IS THE POINT. TC2 shipped
// `CTS_SHIPPED_TO_RULED` mapping V1039's six values onto these eight, marked "meant to be
// deleted". V1040 migrated the column, so the database now emits these names directly and the map
// would be an identity function — a translation layer that no longer translates is a place for a
// future value to get silently absorbed. It is deleted rather than left as a no-op.
//
// ⚠ AND THIS IS NOT THE LEGACY `AssignmentState`. That type (types/compliance-assignments.ts)
// belongs to the `compliance_control_assignments` machine, whose terminal states are
// implemented / compensating_control / not_applicable, and which runs in parallel until TC6. The
// two share four spellings — `unassigned`, `assigned`, `in_progress`, `submitted` — and are NOT
// the same lifecycle. They must never be merged, and a rename in one must never be applied to the
// other by grep.

export const CTS_WORKFLOW_STATES = [
  'unassigned',
  'assigned',
  'in_progress',
  'submitted_for_review',
  'returned',
  'approved',
  'completed',
  'reopened',
] as const;

export type CtsWorkflowState = typeof CTS_WORKFLOW_STATES[number];

export const CTS_WORKFLOW_LABEL: Record<CtsWorkflowState, string> = {
  unassigned: 'Unassigned',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  submitted_for_review: 'Submitted for Review',
  returned: 'Returned',
  approved: 'Approved',
  completed: 'Completed',
  reopened: 'Reopened',
};

/**
 * ⚠ `unassigned` IS NEVER STORED. It is the ABSENCE of an open assignment row.
 *
 * Storing it would mean a `cts_assignments` row whose status asserts that no assignment exists —
 * incoherent, not merely duplicative, and a contradiction the schema would carry forever. V1040's
 * CHECK admits the value for vocabulary completeness and its self-verify asserts that nothing
 * writes it.
 */
export const CTS_UNSTORED_STATES: readonly CtsWorkflowState[] = ['unassigned'];

/**
 * Work that is finished and needs no further action.
 *
 * ⚠ CAPR S1 — `approved` IS FINISHED. An approval is the decision (the durable `approved` event in
 * `cts_assessment`); there is no "mark complete" step after it, and a correction is a NEW assignment.
 * `completed` stays here for the legacy rows that reached it before S1; no new work can.
 */
export const CTS_TERMINAL_STATES: readonly CtsWorkflowState[] = ['approved', 'completed'];

/** Still in flight — what "open" means for a queue, a count, or a workload figure. */
export const CTS_OPEN_STATES: readonly CtsWorkflowState[] = [
  'assigned', 'in_progress', 'submitted_for_review', 'returned', 'reopened',
];

// ── Who may move the work ─────────────────────────────────────────────────────────────────────

/**
 * ⚠ TWO ROLES, AND THE SPLIT IS THE APPROVAL FLOW. `assignee` is whoever the work is assigned to;
 * `reviewer` is the risk_manager/super_admin floor. A machine where the same person can submit and
 * approve is not an approval flow — it is a status field with extra steps.
 */
export type CtsActor = 'assignee' | 'reviewer';

export interface CtsTransition {
  from: CtsWorkflowState;
  to: CtsWorkflowState;
  by: CtsActor;
  /** ⚠ `returned` is ONE state carrying a reason payload — never two states. */
  requiresReason?: true;
}

/**
 * ⚠ THE MACHINE IS ONE INSERT AND EIGHT UPDATES, AND THE ASYMMETRY IS REAL RATHER THAN TIDY.
 *
 * `Unassigned → Assigned` is the CREATION of a row, not a transition of one: before it, no
 * assignment exists to move. Every other movement is an UPDATE of an existing row. A machine that
 * treated all eight states uniformly would have to invent the incoherent `unassigned` row in order
 * to have something to update — which is exactly the drift V1040 asserts against.
 *
 * ⓘ The kickoff described this as "7 updates + 1 insert", counting the arrows of the LINEAR chain
 * Unassigned→…→Reopened. The implemented graph has eight update-arrows because the lifecycle is not
 * purely linear: `submitted_for_review` BRANCHES (approved | returned), and `returned` and
 * `reopened` both LOOP back to `in_progress`. The load-bearing claim — one creation, the rest
 * movements — is unchanged; the count differs and is stated rather than quietly matched.
 */
/*
 * ⚠ CAPR S1 (docs/sprints/capr-s1-foundation-plan.md) — `approved` IS TERMINAL.
 *   • `approved → completed` and `completed → reopened` are GONE. `completed` / `reopened` stay in
 *     the vocabulary and the V1040 CHECK (legacy rows remain valid) but no new work can reach them.
 *   • `reopened → in_progress` STAYS, and only for the legacy rows: a pre-S1 `reopened` row is still
 *     OPEN (V1051's one-open-per-control index), so removing its only exit would wedge that control
 *     forever. Nothing can enter `reopened` any more, so the edge cannot be reached by new work.
 *   • `returned → in_progress → submitted_for_review → approved` is unchanged: a return and its
 *     resubmission are the SAME assignment id. A post-approval correction is a NEW assignment.
 */
export const CTS_TRANSITIONS: readonly CtsTransition[] = [
  { from: 'assigned', to: 'in_progress', by: 'assignee' },
  { from: 'in_progress', to: 'submitted_for_review', by: 'assignee' },
  { from: 'submitted_for_review', to: 'approved', by: 'reviewer' },
  { from: 'submitted_for_review', to: 'returned', by: 'reviewer', requiresReason: true },
  { from: 'returned', to: 'in_progress', by: 'assignee' },
  { from: 'reopened', to: 'in_progress', by: 'assignee' },
];

/**
 * The states from which work may CHANGE HANDS.
 *
 * ⚠ Reassign is deliberately absent from {@link CTS_TRANSITIONS}: it changes WHO holds the work
 * and leaves the state untouched, so modelling it as a transition would mean inventing same-state
 * arrows and every `ctsNextStates()` consumer would start offering it as a move.
 *
 * ⚠ NOT derived as "open minus submitted_for_review". Each exclusion has its own reason —
 * `submitted_for_review` has a decision in flight and the submitter is the person who made the
 * claim; `approved` and `completed` are decided, and swapping the name on finished work
 * re-attributes it. A derived set would silently admit any future open state.
 */
export const CTS_REASSIGNABLE_STATES: readonly CtsWorkflowState[] = [
  'assigned', 'in_progress', 'returned', 'reopened',
];

/** The state a newly created assignment starts in — the one reached by INSERT. */
export const CTS_INITIAL_STATE: CtsWorkflowState = 'assigned';

export function ctsTransition(from: CtsWorkflowState, to: CtsWorkflowState): CtsTransition | undefined {
  return CTS_TRANSITIONS.find((t) => t.from === from && t.to === to);
}

/** Every state reachable from here — what a surface should offer, and nothing more. */
export function ctsNextStates(from: CtsWorkflowState): CtsTransition[] {
  return CTS_TRANSITIONS.filter((t) => t.from === from);
}

export function ctsWorkflowLabel(state: string): string {
  return CTS_WORKFLOW_LABEL[state as CtsWorkflowState]
    // ⚠ An unknown value renders as ITSELF rather than as a default state. A value the vocabulary
    //    does not know is a fact about the data, and showing it is how anyone finds out;
    //    substituting a plausible state would hide exactly the drift worth seeing.
    ?? state.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
}
